import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import Navbar from "@/components/Navbar";
import DeadlineBanner from "@/components/DeadlineBanner";
import FilePreviewHost from "@/components/FilePreviewHost";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ApplicationForm from "@/pages/ApplicationForm";
import SuccessScreen from "@/pages/SuccessScreen";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminApplicationDetail from "@/pages/admin/AdminApplicationDetail";
import OfficerLogin from "@/pages/officer/OfficerLogin";
import AccountantDashboard from "@/pages/accountant/AccountantDashboard";
import OfficerDashboard from "@/pages/officer/OfficerDashboard";
import OfficerAppDetail from "@/pages/officer/OfficerAppDetail";
import OfficerApplications from "@/pages/officer/OfficerApplications";
import OfficerAppView from "@/pages/officer/OfficerAppView";
import OfficerAdmin from "@/pages/officer/OfficerAdmin";
import VacancyDashboard from "@/pages/officer/VacancyDashboard";
import CredentialsSheet from "@/pages/officer/CredentialsSheet";
import ForcePwChange from "@/pages/officer/ForcePwChange";

const OFFICER_ROLES = ["collector", "rdc", "aro", "sdo", "tehsildar", "circle", "viewer"];

function homeFor(role) {
  if (role === "admin") return "/admin";
  if (role === "accountant") return "/accountant";
  if (OFFICER_ROLES.includes(role)) return "/officer/dashboard";
  return "/apply";
}

function Loader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f6f2e6] text-[15px] text-slate-500">
      लोड होत आहे...
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user, loading } = useApp();
  const location = useLocation();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (OFFICER_ROLES.includes(user.role)) return <Navigate to="/officer/dashboard" replace />;
  if (user.role === "accountant") return <Navigate to="/accountant" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/apply" replace />;
  if (!adminOnly && user.role === "admin") return <Navigate to="/admin" replace />;
  return (
    <>
      {!adminOnly && <Navbar />}
      {children}
    </>
  );
}

function OfficerProtected({ children, collectorOnly }) {
  const { user, loading } = useApp();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/officer" replace />;
  if (!OFFICER_ROLES.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  if (user.mustChangePassword) return <ForcePwChange />;
  if (collectorOnly && user.role !== "collector") return <Navigate to="/officer/dashboard" replace />;
  return children;
}

function AccountantProtected({ children }) {
  const { user, loading } = useApp();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/officer" replace />;
  if (user.role !== "accountant") return <Navigate to={homeFor(user.role)} replace />;
  if (user.mustChangePassword) return <ForcePwChange />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useApp();
  if (loading) return <Loader />;
  if (user) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <DeadlineBanner />
          <Routes>
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
            <Route path="/apply" element={<Protected><ApplicationForm /></Protected>} />
            <Route path="/success" element={<Protected><SuccessScreen /></Protected>} />
            <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
            <Route path="/admin/:id" element={<Protected adminOnly><AdminApplicationDetail /></Protected>} />
            <Route path="/officer" element={<PublicOnly><OfficerLogin /></PublicOnly>} />
            <Route path="/officer/dashboard" element={<OfficerProtected><OfficerDashboard /></OfficerProtected>} />
            <Route path="/officer/applications" element={<OfficerProtected><OfficerApplications /></OfficerProtected>} />
            <Route path="/officer/app/:id" element={<OfficerProtected><OfficerAppDetail /></OfficerProtected>} />
            <Route path="/officer/view/:id" element={<OfficerProtected><OfficerAppView /></OfficerProtected>} />
            <Route path="/officer/admin" element={<OfficerProtected collectorOnly><OfficerAdmin /></OfficerProtected>} />
            <Route path="/officer/credentials" element={<OfficerProtected collectorOnly><CredentialsSheet /></OfficerProtected>} />
            <Route path="/officer/vacancy" element={<OfficerProtected><VacancyDashboard /></OfficerProtected>} />
            <Route path="/accountant" element={<AccountantProtected><AccountantDashboard /></AccountantProtected>} />
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
        <FilePreviewHost />
        <Toaster position="top-right" richColors />
      </AppProvider>
    </div>
  );
}

export default App;
